var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useCombobox } from 'downshift';
import { usePopper } from 'react-popper';
import { Box } from 'rebass';
import mergeRefs from 'react-merge-refs';
import { Card, Button, Icon, Input, Option, Separator } from '../';
var escapeRegex = function (s) { return s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'); };
var stateChangeTypes = useCombobox.stateChangeTypes;
var DropdownIndicator = React.forwardRef(function (props, ref) { return (React.createElement(Button, __assign({ variant: "plainDropdown", ref: ref }, props),
    React.createElement(Icon, { icon: "chevron-down", fontSize: "12px" }))); });
var Menu = React.forwardRef(function (props, ref) { return (React.createElement(Card, __assign({ p: 0, m: 0, boxShadow: "selectBox", sx: {
        overflow: 'hidden',
        border: 0,
        zIndex: 99999,
        display: 'inline-block',
    }, ref: ref }, props))); });
var CreateButton = function (_a) {
    var onClick = _a.onClick, onHover = _a.onHover, children = _a.children;
    return (React.createElement(React.Fragment, null,
        React.createElement(Separator, null),
        React.createElement(Option, { onClick: onClick, onMouseMove: onHover, tabIndex: 0, variant: "primary" }, children)));
};
export default function Select(_a) {
    var block = _a.block, _b = _a.createNewText, createNewText = _b === void 0 ? 'Create new' : _b, disabled = _a.disabled, fullWidth = _a.fullWidth, fullHeight = _a.fullHeight, hideSelected = _a.hideSelected, invalid = _a.invalid, _c = _a.items, items = _c === void 0 ? [] : _c, _d = _a.itemsAreEqual, itemsAreEqual = _d === void 0 ? function (a, b) { return (!a || !b ? a === b : a.value === b.value); } : _d, _e = _a.itemToString, itemToString = _e === void 0 ? function (i) { return (!i ? '' : i.label); } : _e, menuIsOpen = _a.menuIsOpen, onBackspaceEmpty = _a.onBackspaceEmpty, onChange = _a.onChange, onCreate = _a.onCreate, onEscape = _a.onEscape, onMenuOpen = _a.onMenuOpen, onReturn = _a.onReturn, placeholder = _a.placeholder, portalRef = _a.portalRef, prefix = _a.prefix, _f = _a.renderItem, renderItem = _f === void 0 ? function (item, label) { return label; } : _f, _suffix = _a.suffix, _g = _a.toggleButtonProps, toggleButtonProps = _g === void 0 ? {} : _g, value = _a.value, props = __rest(_a, ["block", "createNewText", "disabled", "fullWidth", "fullHeight", "hideSelected", "invalid", "items", "itemsAreEqual", "itemToString", "menuIsOpen", "onBackspaceEmpty", "onChange", "onCreate", "onEscape", "onMenuOpen", "onReturn", "placeholder", "portalRef", "prefix", "renderItem", "suffix", "toggleButtonProps", "value"]);
    var _h = React.useState(items), inputItems = _h[0], setInputItems = _h[1];
    var _j = React.useState(''), query = _j[0], setQuery = _j[1];
    var _k = React.useState(value || undefined), selectedItem = _k[0], setSelectedItem = _k[1];
    React.useEffect(function () {
        var pattern = new RegExp(escapeRegex(query || ''), 'i');
        setInputItems(items.filter(function (i) { return itemToString(i).match(pattern); }));
    }, [query, items]);
    var _l = React.useState(null), referenceElement = _l[0], setReferenceElement = _l[1];
    var _m = React.useState(null), popperElement = _m[0], setPopperElement = _m[1];
    var _o = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        modifiers: [
            {
                name: 'flip',
                options: {
                    fallbackPlacements: ['bottom-end', 'top-start', 'top-end', 'right', 'left'],
                },
            },
            {
                name: 'offset',
                options: {
                    offset: [0, 8],
                },
            },
        ],
    }), styles = _o.styles, attributes = _o.attributes, update = _o.update;
    var _p = useCombobox({
        items: inputItems,
        inputValue: query,
        itemToString: itemToString,
        selectedItem: selectedItem,
        onStateChange: function (changes) {
            switch (changes.type) {
                case stateChangeTypes.InputChange:
                    update && update();
                    setQuery(changes.inputValue || '');
                    return;
                case stateChangeTypes.InputKeyDownEnter:
                case stateChangeTypes.ItemClick:
                case stateChangeTypes.FunctionSelectItem:
                    setQuery('');
                    // If this is a multiselect and this item was the last one removed, we get an input value
                    // but no selected item. Until we can nail that down, this should suffice.
                    var item = changes.selectedItem
                        ? changes.selectedItem
                        : items.find(function (i) { return itemToString(i) == changes.inputValue; });
                    onChange(item || null);
                    setSelectedItem(item);
                    closeMenu();
                    return;
                case stateChangeTypes.FunctionOpenMenu:
                    if (changes.isOpen) {
                        update && update();
                        onMenuOpen && onMenuOpen();
                    }
                    return;
            }
        },
    }), closeMenu = _p.closeMenu, getComboboxProps = _p.getComboboxProps, getInputProps = _p.getInputProps, getItemProps = _p.getItemProps, getMenuProps = _p.getMenuProps, getToggleButtonProps = _p.getToggleButtonProps, highlightedIndex = _p.highlightedIndex, isOpen = _p.isOpen, openMenu = _p.openMenu, selectItem = _p.selectItem, setHighlightedIndex = _p.setHighlightedIndex;
    var suffix = (React.createElement(React.Fragment, null,
        _suffix,
        React.createElement(DropdownIndicator, __assign({}, getToggleButtonProps(__assign(__assign(__assign({}, toggleButtonProps), { disabled: disabled }), (props.variant === 'inverse' ? { variant: 'plain' } : {}))), { ml: 3 }))));
    var portal = ReactDOM.createPortal(React.createElement(Menu, __assign({}, getMenuProps({ ref: mergeRefs([setPopperElement, portalRef || null]) }), attributes.popper, { style: styles.popper }), (isOpen || menuIsOpen) && (React.createElement(React.Fragment, null,
        React.createElement(Box, { sx: {
                maxHeight: '240px',
                overflow: 'auto',
                display: 'grid',
                width: ((referenceElement === null || referenceElement === void 0 ? void 0 : referenceElement.clientWidth) || 0) + "px",
                minWidth: '240px',
            } }, inputItems.map(function (item, index) {
            var selected = itemsAreEqual(selectedItem, item);
            var highlighted = highlightedIndex === index;
            if (selected && hideSelected)
                return null;
            var label = itemToString(item);
            return (React.createElement(Option, __assign({ isSelected: selected, isFocused: highlighted, key: "" + label + index, children: renderItem(item, label) }, getItemProps({ item: item, index: index, disabled: disabled, onMouseDown: function () { return selectItem(item); } }))));
        })),
        onCreate && (React.createElement(CreateButton, { onClick: function () { return onCreate(query); }, onHover: function () { return setHighlightedIndex(-1); } }, createNewText))))), document.body);
    return (React.createElement(React.Fragment, null,
        React.createElement(Input, __assign({ block: block, prefix: prefix, suffix: suffix, containerProps: getComboboxProps({ ref: setReferenceElement }), fullWidth: fullWidth, fullHeight: fullHeight, invalid: invalid, placeholder: value ? renderItem(value, itemToString(value)) : placeholder, darkenPlaceholder: !!value }, getInputProps({
            disabled: disabled,
            onKeyDown: function (event) {
                switch (event.key) {
                    case 'Enter':
                        if (!isOpen && onReturn)
                            onReturn();
                        return;
                    case 'Escape':
                        if (!isOpen && onEscape)
                            onEscape();
                        return;
                    case 'Backspace':
                        if (query === '' && onBackspaceEmpty)
                            onBackspaceEmpty();
                        return;
                }
            },
            onFocus: openMenu,
        }), props)),
        portal));
}
